import { publicApi } from "@api";
import { IconMSIContentCopy } from "@assets";
import { envConfig } from "@configs/env";
import useAuthSession from "@hooks/useAuthSession";
import usePreference from "@hooks/usePreference";
import preferenceSlice from "@stores/preference/slices";
import { CopyWrapper, PropsWithClassName, cn } from "kz-ui-sdk";
import { useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

interface UsernameBadgeProps extends PropsWithClassName {
  innerClassName?: string;
  textClassName?: string;
  copyWrapperClassName?: string;
  iconClassName?: string;
  mode?: "apiIdentifier" | "accessKey";
}

const UsernameBadge = ({
  className,
  innerClassName,
  textClassName,
  copyWrapperClassName,
  iconClassName,
  mode = "apiIdentifier",
}: UsernameBadgeProps) => {
  const { t } = useTranslation();
  const { serverInfo, isLoading } = usePreference();
  const { self, credential } = useAuthSession();
  const dispatch = useDispatch();
  const [getInfo] = publicApi.useLazyGetInfoQuery();

  useEffect(() => {
    // fetch info if not loading and no prefix
    if (!isLoading && !serverInfo?.prefix) {
      console.log("fetching info manually");
      getInfo({ accountId: envConfig.accountId })
        .unwrap()
        .then((res) => {
          dispatch(preferenceSlice.actions.setServerInfo(res));
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [isLoading, serverInfo?.prefix, getInfo, dispatch]);

  const { username, successToastMsg, failedToastMsg } = useMemo(() => {
    if (mode === "apiIdentifier") {
      return {
        username: `${serverInfo?.prefix}${self?.member?.apiIdentifier}`,
        successToastMsg: t("ID Copied!"),
        failedToastMsg: t("Failed to copy ID!"),
      };
    }
    return {
      username: credential?.accessKey ?? "",
      successToastMsg: t("username copied"),
      failedToastMsg: t("failed to copy username"),
    };
  }, [mode, serverInfo?.prefix, self?.member?.apiIdentifier, credential?.accessKey, t]);

  return (
    <div className={cn(className)}>
      <CopyWrapper
        text={username}
        onSuccess={() => {
          toast.success(successToastMsg);
        }}
        onError={() => {
          toast.error(failedToastMsg);
        }}
        className={cn("group bg-transparent bg-none", copyWrapperClassName)}
      >
        <div className={cn("flex items-center gap-2 p-1 px-0 transition-opacity active:opacity-70", innerClassName)}>
          <span className={cn("text-lg text-content-base", textClassName)}>{username}</span>{" "}
          <IconMSIContentCopy
            className={cn("scale-125 text-content-soft transition-transform group-active:!scale-110", iconClassName)}
          />
        </div>
      </CopyWrapper>
    </div>
  );
};

export default UsernameBadge;
